import { effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { storeFeatures } from '@core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { AuthActions, AuthSelectors } from '@ngrx-store';
import { NotificationsService } from '@ui';

import { TotpService } from '../../services/totp.service';

const BANNER_DISMISSED_KEY = 'totpRequiredBannerDismissed';

interface TotpAuthState {
  bannerDismissed: boolean;
  currentCodeVerified: boolean;
  otpauthUrl: string | null;
  recoveryCodes: string[] | null;
  secret: string | null;
}

const initialState: Omit<TotpAuthState, 'bannerDismissed'> = {
  currentCodeVerified: false,
  otpauthUrl: null,
  recoveryCodes: null,
  secret: null,
};

export const TotpAuthStore = signalStore(
  { providedIn: 'root' },
  withState<TotpAuthState>({ ...initialState, bannerDismissed: false }),
  storeFeatures.withRequestStatus(),
  withMethods((
    store,
    dialog = inject(MatDialog),
    ngrxStore = inject(Store),
    notificationService = inject(NotificationsService),
    totpService = inject(TotpService),
  ) => ({
    clearError() {
      patchState(store, { requestStatus: 'idle' });
    },

    completeEnrollment() {
      patchState(store, initialState);
      dialog.closeAll();
      notificationService.showSuccessMessage('Two-factor authentication enabled');
    },

    confirm(token: string) {
      store.setPending();

      totpService.confirm(token).subscribe({
        error: error => store.setError(error.message),
        next: result => {
          store.setFulfilled();
          patchState(store, { recoveryCodes: result.recoveryCodes });
          ngrxStore.dispatch(AuthActions.refreshProfile());
        },
      });
    },

    dismissBanner() {
      patchState(store, { bannerDismissed: true });
      localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    },

    enroll() {
      store.setPending();

      totpService.enroll().subscribe({
        error: error => store.setError(error.message),
        next: enrollment => {
          store.setFulfilled();
          patchState(store, { otpauthUrl: enrollment.otpauthUrl, secret: enrollment.secret });
        },
      });
    },

    reset() {
      patchState(store, { ...initialState, requestStatus: 'idle' });
    },

    verifyCurrent(token: string) {
      patchState(store, { currentCodeVerified: false });
      store.setPending();

      totpService.verifyCurrent(token).subscribe({
        error: error => store.setError(error.message),
        next: () => {
          store.setFulfilled();
          patchState(store, { currentCodeVerified: true });
        },
      });
    },
  })),
  withHooks({
    onInit(store) {
      const ngrxStore = inject(Store);

      patchState(store, { bannerDismissed: localStorage.getItem(BANNER_DISMISSED_KEY) === 'true' });

      const isLoggedIn = toSignal(ngrxStore.select(AuthSelectors.isLoggedIn));

      effect(() => {
        if (isLoggedIn() === false) {
          patchState(store, { bannerDismissed: false });
          localStorage.removeItem(BANNER_DISMISSED_KEY);
        }
      });
    },
  }),
);
